/*
 * Authors: mariushirjanu
 * Created: 28/07/2022
 */
import * as _ from 'underscore';
import Noty from 'noty';
import { UntypedFormGroup } from '@angular/forms';
import { Component, OnDestroy, OnInit, Input } from '@angular/core';
import { util } from '../../../shared/util';
import { GlobalProductService } from '../global-product.service';
import { GaFormlyService, IExtraFieldConfigurationObject } from '../../../shared/formly/ga-formly.service';
import { GlobalProductDataService, IGlobalItem, IProductCategory } from '../global-product-data.service';
import { GaMessageService } from '../../../shared/message/ga-message.service';
import { GaValidateService } from '../../../shared/formly/ga-validate.service';
import { ICompany, IDocument, IObject } from '../../../../type_definitions/datatypes';
import { FormattingService } from '../../../shared/services/formatting.service';
import { TranslationService } from '../../../shared/translation/translation.service';
import { FileUploadManageComponent } from '../../../shared/file-upload/file-upload-manage.component';
import { GLOBAL_PRODUCT_SAVE_ACTION, GlobalProductEditService } from './global-product-edit.service';
import { STATE_GLOBALPRODUCT_EDIT_GENERAL } from '../../../shared/app-states';

import { StateService, Transition, TransitionService } from '@uirouter/angular';
import { EVENT_SAVE_GLOBALPRODUCT } from '../../../shared/app-events';
import { GaEventService } from '../../../shared/services/ga-event.service';
import { GaWizard, IWizardButton } from '../../../shared/wizard/ga-wizard';
import { GaWizardService } from '../../../shared/wizard/ga-wizard.service';
import { WIZARD_GLOBAL_PRODUCT_EDIT } from '../../../shared/app-wizards';

@Component({
  selector: 'global-product-edit-general',
  template: `
    <h2>General Info</h2>
    <formly [form]="form" [fields]="fields" [model]="model"></formly>
  `
})
export class GlobalProductEditGeneralComponent implements OnInit, OnDestroy {
  @Input() public newProduct:boolean;

  private wizard:GaWizard;
  private wizardId:string = WIZARD_GLOBAL_PRODUCT_EDIT;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public fields:IExtraFieldConfigurationObject[];
  public model:IGlobalItem;

  private stateAfterSave:IObject;
  private modelField:IExtraFieldConfigurationObject;
  private subCategoryField:IExtraFieldConfigurationObject;
  private categoryField:IExtraFieldConfigurationObject;
  private vendorField:IExtraFieldConfigurationObject;
  private uploadDocumentField:IExtraFieldConfigurationObject;

  private labelClass:string = 'col-xsm-4 col-sm-3 col-md-3';
  private valueClass:string = 'col-xsm-8 col-sm-9 col-md-9';

  constructor(
    private validateService:GaValidateService,
    private gaEventService:GaEventService,
    private globalProductDataService:GlobalProductDataService,
    private formlyService:GaFormlyService,
    private globalProductService:GlobalProductService,
    private globalProductEditService:GlobalProductEditService,
    private messageService:GaMessageService,
    private formattingService:FormattingService,
    private translationService:TranslationService,
    private stateService:StateService,
    private wizardService:GaWizardService,
    private transitionService:TransitionService
  ) {
    this.wizard = wizardService.getInstance(this.wizardId);
  }

  public ngOnInit():void {
    this.start();
    this.listen();
  }

  private start = ():void => {
    this.model = this.globalProductService.product() || <any>{};
    this.initWizardSetup();

    this.fields = this.createFields();
    this.fillFields();

    this.onVendorChange();
    this.onCategoryChange();
  };

  private fillFields(): void {
    this.formlyService.fillField(this.categoryField, this.globalProductDataService.categories());
    this.formlyService.fillField(this.vendorField, this.globalProductDataService.vendors());
  }

  private listen(): void {
    this.transitionService.onStart({}, (transition:Transition) => {
      // it is possible that wizardService has prevented the event to go through. Then we should do nothing.
      if (this.form.dirty) {
        transition.abort();

        this.stateAfterSave = {
          name: transition.to().name,
          params: transition.params('to')
        };

        this.messageService.unsavedChangesDialog(this.onSave, this.onDiscard);
      }
    });
  }

  private whenSaved = (updatedProduct:IGlobalItem):void => {
    this.globalProductService.product(updatedProduct);
    this.form.markAsPristine();

    if (this.stateAfterSave) {
      //replace productId in params by real product id
      if (this.stateAfterSave.params?.product) {
        this.stateAfterSave.params.product = updatedProduct.id;
      }
      this.stateService.go(this.stateAfterSave.name, this.stateAfterSave.params);
    }
  };

  private createFields():IExtraFieldConfigurationObject[] {
    return [
      this.vendorField = this.formlyService.aSelect('vendor', 'Vendor')
        .isRequired()
        .withFormat(this.formattingService.formatName)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .onChange(this.onVendorChange)
        .build(),

      this.formlyService.anInput('globalPart', 'Config Id')
        .isRequired()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.categoryField = this.formlyService.aSelect('category', 'Category')
        .isRequired()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withFormat(this.formattingService.formatDescription)
        .withExtraOptions({
          templateOptions: {
            trackBy: 'name'
          }
        })
        .onChange(this.onCategoryChange)
        .build(),

      this.subCategoryField = this.formlyService.aSelect('subcategory', 'Subcategory')
        .withFormat(this.formattingService.formatName)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.anInput('categoryDetail', 'Category Detail')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.anInput('url', 'URL')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withInputType('url')
        .withInfo('prefix with http:// or https://')
        .showInfo()
        .build(),

      this.modelField = this.formlyService.aSearch('model', 'Model')
        .withFormat(this.formattingService.formatName)
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .withExtraOptions({
          templateOptions: {
            minSearchLength: 1,
            params: {
              oemId: this.model?.vendor?.id
            }
          }
        })
        .withUrl('rest/globalproduct/models')
        .build(),

      this.formlyService.anInput('shortDescription', 'Short Description')
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.formlyService.aTextarea('description', 'Description')
        .isRequired()
        .withLabelClass(this.labelClass)
        .withValueClass(this.valueClass)
        .build(),

      this.uploadDocumentField = this.formlyService.aCustom('Documentation')
        .withWrapperClass('col-12 documentation')
        .withComponent(FileUploadManageComponent)
        .withExtraOptions({
          templateOptions: {
            inputs: {
              buttonLabel: this.translationService.instant('GA.BUTTONS.ADD_LABEL', { label: this.translationService.instant('GA.GENERAL.DOCUMENT') }),
              uploadUrl: `rest/globalproduct/products/${this.model?.id}/document`,
              existingFile: this.model?.document
            },
            outputs: {
              onUploadDone: (result:IDocument) => {
                this.model.document = result?.id && _.clone(result);
                this.uploadDocumentField.templateOptions.inputs.existingFile = this.model.document;
              },
              onDelete: this.deleteDocument
            }
          }
        })
        .withHideExpression(() => this.newProduct)
        .build()
    ];
  }

  private prepareProductForUpdate():IGlobalItem {
    const updatedProduct:IGlobalItem = util.deepClone(this.model);
    updatedProduct.properties = undefined;
    updatedProduct.freeProperties = undefined;
    return updatedProduct;
  }

  private updateProduct(): void {
    this.stateAfterSave = undefined;
    this.onSave();
  }

  private saveProduct = (): void => {
    this.stateAfterSave = {
      name: STATE_GLOBALPRODUCT_EDIT_GENERAL,
      params: { product: 'willBeReplaced' }
    };
    this.onSave();
  };

  private onSave = ():void => {
    this.validateService.validateFields(this.fields, this.model, true)
      .then(() => {
        const msg:Noty = this.messageService.showStatusMessage('Saving Product');
        const preparedProduct:IGlobalItem = this.prepareProductForUpdate();
        this.wizard.updateActionStatus(GLOBAL_PRODUCT_SAVE_ACTION, true);
        const savePromise:Promise<IGlobalItem> = this.model?.id ? this.globalProductDataService.update(this.model.id, preparedProduct) : this.globalProductDataService.newProduct(preparedProduct);
        savePromise
          .then(this.whenSaved)
          .finally(() => {
            this.wizard.updateActionStatus(GLOBAL_PRODUCT_SAVE_ACTION, false);
            this.messageService.closeMessage(msg);
            if(this.model?.id){
              this.gaEventService.sendMessage(EVENT_SAVE_GLOBALPRODUCT);
            }
          });
      })
      .catch((errorMessages:string[]) => {
        this.globalProductEditService.showInvalidMessage(errorMessages);
      });
  };

  private onDiscard = ():void => {
    this.globalProductService.reset();
    this.form.markAsPristine();
    this.stateService.go(this.stateAfterSave.name, this.stateAfterSave.params);
  };

  private deleteDocument = ():void => {
    this.globalProductDataService.deleteDocument(this.model.id, this.model.document.id)
      .then(() => {
        this.model.document = undefined;
        this.uploadDocumentField.templateOptions.inputs.existingFile = undefined;
      });
  };

  private onVendorChange = (vendor?:ICompany, oldVendor?:ICompany):void => {
    vendor = vendor || this.model?.vendor;

    this.modelField.templateOptions.disabled = !vendor?.id;
    this.modelField.templateOptions.params.oemId = vendor?.id;

    // vendor has been changed by user -> clear the model
    if(oldVendor?.id && vendor?.id !== oldVendor?.id) {
      this.modelField.formControl.patchValue(undefined);
    }
  };

  private onCategoryChange = (category?:IProductCategory):void => {
    category = category || this.model?.category;
    if (category?.name) {
      this.formlyService.fillField(this.subCategoryField, this.globalProductDataService.subCategories(category.name))
        .then(() => {
          this.subCategoryField.templateOptions.disabled = false;
        });
    } else {
      this.subCategoryField.templateOptions.options = [];
      this.subCategoryField.templateOptions.disabled = true;
    }
  };

  private initWizardSetup():void {
    const wizardActions:IWizardButton[] = [];
    wizardActions.push(
      this.wizardService.createActionButton(
        GLOBAL_PRODUCT_SAVE_ACTION,
        this.model?.id ? `Save ${this.wizard.activeStepTitle()}` : 'Save Product',
        'btn btn-success',
        this.saveProduct
      )
    );

    this.wizard.actions(wizardActions);
  }

  public ngOnDestroy():void {
    this.wizard.clearActions();
  }
}